<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { PaymentMethod } from '../services/payment-method.service'
import paymentMethodService from '../services/payment-method.service'

const props = defineProps<{
  paymentMethod: PaymentMethod
  canEdit: boolean
}>()

const emit = defineEmits<{
  edit: []
  delete: []
}>()

const { t, locale } = useI18n()

const getProviderName = (provider: string) => {
  return paymentMethodService.getProviderName(provider as any)
}

const getEnvironmentName = (environment: string) => {
  return paymentMethodService.getEnvironmentName(environment)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString(({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<template>
  <div class="border-2 border-gray-300 rounded-lg p-5 bg-white shadow-sm">
    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
      <div class="flex-1">
        <div class="flex items-center gap-3 mb-3">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ getProviderName(paymentMethod.provider) }}
          </h3>
          <span
            :class="[
              'px-2 py-1 text-xs font-medium rounded',
              paymentMethod.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            ]"
          >
            {{ paymentMethod.is_active ? t('paymentMethodCard.active') : t('paymentMethodCard.inactive') }}
          </span>
        </div>

        <div class="space-y-2 text-sm">
          <div class="flex items-center text-gray-600">
            <span class="font-medium w-32">{{ t('paymentMethodCard.environment') }}</span>
            <span class="text-gray-900">
              {{ getEnvironmentName(paymentMethod.configuration.environment || '') }}
            </span>
          </div>

          <!-- PayU specific -->
          <div v-if="paymentMethod.provider === 'PAYU'" class="flex items-start text-gray-600">
            <span class="font-medium w-32 flex-shrink-0">Merchant POS ID:</span>
            <span class="text-gray-900 break-all">{{ paymentMethod.configuration.merchant_pos_id }}</span>
          </div>

          <!-- Stripe specific -->
          <div v-if="paymentMethod.provider === 'STRIPE'" class="flex items-start text-gray-600">
            <span class="font-medium w-32 flex-shrink-0">Publishable Key:</span>
            <span class="text-gray-900 font-mono text-xs break-all">{{ paymentMethod.configuration.publishable_key }}</span>
          </div>

          <!-- Przelewy24 specific -->
          <div v-if="paymentMethod.provider === 'PRZELEWY24'" class="flex items-start text-gray-600">
            <span class="font-medium w-32 flex-shrink-0">Merchant ID:</span>
            <span class="text-gray-900 break-all">{{ paymentMethod.configuration.merchant_id }}</span>
          </div>

          <div class="flex items-center text-gray-500 text-xs pt-2 border-t border-gray-200">
            <span>{{ t('paymentMethodCard.createdAt') }} {{ formatDate(paymentMethod.created_at) }}</span>
            <span v-if="paymentMethod.updated_at !== paymentMethod.created_at" class="ml-4">
              {{ t('paymentMethodCard.updatedAt') }} {{ formatDate(paymentMethod.updated_at) }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="canEdit" class="flex gap-2 w-full lg:w-auto">
        <button
          @click="emit('edit')"
          class="flex-1 lg:flex-none px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 transition-colors"
        >
          {{ t('common.edit') }}
        </button>
        <button
          @click="emit('delete')"
          class="flex-1 lg:flex-none px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 transition-colors"
        >
          {{ t('common.delete') }}
        </button>
      </div>
    </div>
  </div>
</template>
